// Extra werken en aanrekenbare kosten aan de onderaannemer (SPEC_wijzigingen 2–4).
// Eén datamodel: elke regel heeft drie losse eigenschappen —
//   verrekend op (de calculatie), toegerekend aan (een P-nummer), telt mee in percentage.
import { z } from 'zod';
import { round2 } from './schijven.js';
import { opleveringSchema } from './opleveringSchema.js';

export const extraRegelSoortSchema = z.enum(['extra_werk', 'aanrekenbare_kost']);
export type ExtraRegelSoort = z.infer<typeof extraRegelSoortSchema>;

export const EXTRA_WERK_CATEGORIEEN = [
  'CORRECTIEWERK',
  'HERSTELWERK',
  'ONAFGEWERKT_ANDERE_OA',
  'UITZONDERLIJK',
  'OVERIG',
] as const;

export const AANREKENBARE_KOST_CATEGORIEEN = [
  'SCHADE',
  'FRANCHISE',
  'COMMERCIELE_TEGEMOETKOMING',
  'BOETE',
  'MINWERKEN',
  // Bestaande categorieën van vóór oktober 2026 — blijven geldig voor gemigreerde regels.
  'INHOUDING',
  'ANDERE',
] as const;

export const CATEGORIE_LABELS: Record<string, string> = {
  CORRECTIEWERK: 'Correctiewerk',
  HERSTELWERK: 'Herstelwerk',
  ONAFGEWERKT_ANDERE_OA: 'Onafgewerkt door andere onderaannemer',
  UITZONDERLIJK: 'Uitzonderlijk werk op specifiek project',
  OVERIG: 'Overig',
  SCHADE: 'Schade',
  FRANCHISE: 'Franchise verzekering',
  COMMERCIELE_TEGEMOETKOMING: 'Commerciële tegemoetkoming',
  BOETE: 'Boete',
  MINWERKEN: 'Minwerken',
  INHOUDING: 'Inhouding',
  ANDERE: 'Andere',
};

export const extraRegelBronSchema = z.enum(['manueel', 'bouwflow', 'verrekening_6_5']);
export type ExtraRegelBron = z.infer<typeof extraRegelBronSchema>;

export const extraRegelSchema = z.object({
  id: z.string(),
  soort: extraRegelSoortSchema,
  categorie: z.string(),
  omschrijving: z.string(),
  /** Excl. btw, altijd positief; het teken volgt uit de soort. */
  bedrag: z.number().nonnegative(),
  /** P-nummer van het project waar de regel bij hoort. Leeg = het project van de calculatie. */
  toegerekendProject: z.string().default(''),
  /** Verplichte, bewuste keuze zonder voorselectie. null = nog niet gekozen (10.3). */
  teltMeeInPercentage: z.boolean().nullable(),
  bron: extraRegelBronSchema.default('manueel'),
  /** Id van het Bouwflow-document of de calculatie waaruit het bedrag komt. */
  bronReferentie: z.string().nullable().default(null),
  /** Verplicht bij aanrekenbare kosten. */
  reden: z.string().default(''),
  /** Wiens werk werd afgewerkt of gecorrigeerd (3.4) — enkel vastleggen + filter. */
  oorspronkelijkePloegId: z.string().nullable().default(null),
  aangemaaktDoor: z.string().nullable().optional(),
  aangemaaktOp: z.string().optional(),
  /** Open voor oplevering (27okt 4) — enkel voor extra werken. */
  oplevering: opleveringSchema.nullable().optional(),
});
export type ExtraRegel = z.infer<typeof extraRegelSchema>;

/** P-nummer normaliseren voor vergelijking: "p-1234 " → "P-1234". */
export function normalizePNummer(input: string | null | undefined): string {
  const t = (input ?? '').trim().toUpperCase();
  if (!t) return '';
  return t.startsWith('P-') ? t : 'P-' + t.replace(/^-+/, '');
}

/** Is de regel toegerekend aan het project van de calculatie zelf? */
export function isEigenProject(regel: Pick<ExtraRegel, 'toegerekendProject'>, calcProjectNummer: string): boolean {
  const t = normalizePNummer(regel.toegerekendProject);
  return t === '' || t === normalizePNummer(calcProjectNummer);
}

/** +bedrag voor extra werk, −bedrag voor een aanrekenbare kost. */
export function getekendBedrag(regel: Pick<ExtraRegel, 'soort' | 'bedrag'>): number {
  return regel.soort === 'extra_werk' ? regel.bedrag : -regel.bedrag;
}

export function sommeerExtraRegels(regels: ExtraRegel[]): { extraWerk: number; aanrekening: number } {
  let extraWerk = 0;
  let aanrekening = 0;
  for (const r of regels) {
    if (r.soort === 'extra_werk') extraWerk += r.bedrag || 0;
    else aanrekening += r.bedrag || 0;
  }
  return { extraWerk: round2(extraWerk), aanrekening: round2(aanrekening) };
}

/** Validatie per regel — geeft mens-leesbare fouten terug, leeg = in orde. */
export function valideerExtraRegel(r: ExtraRegel): string[] {
  const fouten: string[] = [];
  const geldig: readonly string[] = r.soort === 'extra_werk' ? EXTRA_WERK_CATEGORIEEN : AANREKENBARE_KOST_CATEGORIEEN;
  if (!geldig.includes(r.categorie)) fouten.push(`Onbekende categorie "${r.categorie}"`);
  if (!r.omschrijving.trim()) fouten.push('Omschrijving is verplicht');
  if (!(r.bedrag > 0)) fouten.push('Bedrag moet groter dan 0 zijn');
  if (r.teltMeeInPercentage == null) fouten.push('Kies of de regel meetelt in het percentage');
  if (r.soort === 'aanrekenbare_kost' && !r.reden.trim()) fouten.push('Reden is verplicht bij een aanrekenbare kost');
  // De automatische verrekenregel uit SPEC_workflow 6.5 staat al op het andere project
  // via de correctiecalculatie — vast niet meetellen, anders telt het bedrag dubbel.
  if (r.bron === 'verrekening_6_5' && r.teltMeeInPercentage !== false) {
    fouten.push('Een verrekenregel (6.5) telt nooit mee in het percentage');
  }
  if (r.soort === 'aanrekenbare_kost' && r.oplevering?.status === 'open') {
    fouten.push('Enkel werkposten, meerwerken en extra werken kunnen open staan voor oplevering');
  }
  return fouten;
}

/** Splitst de regels voor de PDF (2.6): eigen project per soort, en regels voor andere projecten. */
export function splitsExtraRegels(regels: ExtraRegel[], calcProjectNummer: string) {
  const eigen = regels.filter((r) => isEigenProject(r, calcProjectNummer));
  const anderProject = regels.filter((r) => !isEigenProject(r, calcProjectNummer));
  return {
    extraWerkEigen: eigen.filter((r) => r.soort === 'extra_werk'),
    kostenEigen: eigen.filter((r) => r.soort === 'aanrekenbare_kost'),
    anderProject,
    /** Netto effect van de regels voor andere projecten op het te factureren bedrag. */
    anderProjectNetto: round2(anderProject.reduce((s, r) => s + getekendBedrag(r), 0)),
  };
}
